import { SERVICES_TAGS } from 'constants/servicesTags';
import initialApiService from 'services/initialApiService';

import {
  PropertyAuditListRequest,
  PropertyAuditListResponse,
} from 'types/admin/propertyAuditTypes';
import { PropertyProcessEnum, PropertyStatusEnum } from 'types/property/propertyEnum';
import {
  AgencyPropertyResponse,
  GetPropertyType,
  InviteResType,
  PropertyFullInfoTypeList,
  PropertyRequestType,
  PropertyType,
  getAdminPropertiesResType,
  getPropertiesReqType,
  getPropertiesResType,
  getPropertyAgenciesResType,
  uploadImageType,
  uploadResourceType,
} from 'types/property/propertyTypes';

const propertyApiService = initialApiService.injectEndpoints({
  endpoints: (builder) => ({
    getPropertyInfo: builder.query<PropertyFullInfoTypeList, string>({
      query: (type) => ({
        url: `/propertytypes/list/${type}`,
        method: 'GET',
      }),
      providesTags: [SERVICES_TAGS.PROPERTY_FULL_INFO],
    }),
    getPropertyAgencies: builder.query<getPropertyAgenciesResType, void>({
      query: () => ({
        url: `/propertyagencies`,
        method: 'GET',
      }),
    }),
    getAgencyProperties: builder.query<
      AgencyPropertyResponse,
      { pageNumber: number; pageSize: number; id: string }
    >({
      query: ({ pageNumber, pageSize, id }) => ({
        url: `propertyagencies/properties`,
        method: 'GET',
        params: { pageNumber, pageSize, id }, // Query parameters
      }),
    }),
    deleteAgent: builder.mutation<PropertyType, { propertyId: string; email: string }>({
      query: (body) => ({
        url: 'properties/agent',
        method: 'DELETE',
        body,
      }),
    }),
    createProperty: builder.mutation<PropertyType, PropertyRequestType>({
      query: (body) => ({
        url: 'properties/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: () => [{ type: SERVICES_TAGS.PROPERTIES, id: 'LIST' }],
    }),
    updateProperty: builder.mutation<PropertyType, PropertyRequestType>({
      query: (data) => ({
        url: `properties/${data.id}/edit`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: () => [{ type: SERVICES_TAGS.PROPERTIES, id: 'LIST' }],
    }),
    approveRejectProperty: builder.mutation<
      PropertyType,
      { propertyId: string; status: PropertyProcessEnum; comments?: string }
    >({
      query: (data) => ({
        url: `/admin/changepropertystatus`,
        method: 'PUT',
        body: data,
      }),
    }),
    hideShowProperty: builder.mutation<PropertyType, { id: string; hide: boolean }>({
      query: (data) => ({
        url: `/properties/visibilitystatus`,
        method: 'PUT',
        body: data,
      }),
    }),
    adminPropertyList: builder.mutation<
      getAdminPropertiesResType,
      { pageNumber: number; pageSize: number; status: PropertyStatusEnum }
    >({
      query: (body) => ({
        url: 'admin/list',
        method: 'POST',
        body,
      }),
    }),
    getPropertyAudits: builder.mutation<PropertyAuditListResponse, PropertyAuditListRequest>({
      query: (body) => ({
        url: '/admin/propertyaudits',
        method: 'POST',
        body,
      }),
    }),
    uploadPropertyImage: builder.mutation<PropertyType, uploadImageType>({
      query: (body) => ({
        url: 'properties/image',
        method: 'POST',
        body,
      }),
    }),
    deletePropertyImages: builder.mutation<PropertyType, string[]>({
      query: (body) => ({
        url: 'properties/images',
        method: 'DELETE',
        body,
      }),
    }),
    uploadPropertyResource: builder.mutation<PropertyType, uploadResourceType>({
      query: (body) => ({
        url: 'properties/resource',
        method: 'POST',
        body,
      }),
    }),
    deletePropertyImage: builder.mutation<PropertyType, string>({
      query: (id) => ({
        url: `properties/image/${id}`,
        method: 'DELETE',
      }),
    }),
    deletePropertyResource: builder.mutation<PropertyType, string>({
      query: (id) => ({
        url: `properties/resource/${id}`,
        method: 'DELETE',
      }),
    }),
    deleteProperty: builder.mutation<PropertyType, string>({
      query: (id) => ({
        url: `properties/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: SERVICES_TAGS.PROPERTIES, id: 'LIST' }],
    }),
    deleteAllProperties: builder.mutation<any, void>({
      query: () => ({
        url: `properties/deleteall`,
        method: 'DELETE',
      }),
    }),
    getProperties: builder.query<getPropertiesResType, getPropertiesReqType>({
      query: (body) => ({
        url: 'properties/list',
        method: 'POST',
        body,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.properties.map(({ id }) => ({
                type: SERVICES_TAGS.PROPERTIES,
                id,
              })),
              { type: SERVICES_TAGS.PROPERTIES, id: 'LIST' },
            ]
          : [{ type: SERVICES_TAGS.PROPERTIES, id: 'LIST' }],
    }),
    reportProperty: builder.mutation<GetPropertyType, { id: string; comments: string }>({
      query: (body) => ({
        url: 'properties/report',
        method: 'POST',
        body,
      }),
    }),
    watchProperty: builder.mutation<getPropertiesResType, string>({
      query: (id) => ({
        url: `users/watch/${id}`,
        method: 'POST',
      }),
      invalidatesTags: () => [{ type: SERVICES_TAGS.WATCHED_PROPERTIES, id: 'LIST' }],
    }),
    unWatchProperty: builder.mutation<getPropertiesResType, string>({
      query: (id) => ({
        url: `users/unwatch/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: SERVICES_TAGS.WATCHED_PROPERTIES, id: 'LIST' }],
    }),
    getWatchingProperty: builder.query<getPropertiesResType, void>({
      query: () => ({
        url: `users/watching`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result ? [{ type: SERVICES_TAGS.WATCHED_PROPERTIES, id: 'LIST' }] : [],
    }),
    getPropertyDetail: builder.query<PropertyType, string>({
      query: (id) => ({
        url: `/properties/detail/${id}`,
        method: 'GET',
      }),
      providesTags: [SERVICES_TAGS.PROPERTY_DETAIL],
    }),
    inviteAgent: builder.mutation<
      InviteResType,
      {
        propertyId: string;
        email: string;
        agencyId?: string;
      }
    >({
      query: (body) => ({
        url: `properties/invite`,
        method: 'POST',
        body,
      }),
    }),
    sendListingEmail: builder.mutation<void, { id: string }>({
      query: (body) => ({
        url: 'properties/listingemail',
        method: 'POST',
        body, // expects { id: "string" }
      }),
    }),
    logPhoneReveal: builder.mutation<void, { id: string; contactUserId: string }>({
      query: (body) => ({
        url: 'properties/phonereveal',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetPropertyDetailQuery,
  useHideShowPropertyMutation,
  useWatchPropertyMutation,
  useApproveRejectPropertyMutation,
  useAdminPropertyListMutation,
  useGetPropertyAuditsMutation,
  useLazyGetAgencyPropertiesQuery,
  useUnWatchPropertyMutation,
  useLazyGetPropertyAgenciesQuery,
  useInviteAgentMutation,
  useDeleteAgentMutation,
  useSendListingEmailMutation,
  useReportPropertyMutation,
  useLazyGetPropertyInfoQuery,
  useUploadPropertyImageMutation,
  useUploadPropertyResourceMutation,
  useDeletePropertyResourceMutation,
  useDeletePropertyImageMutation,
  useDeletePropertyImagesMutation,
  useDeleteAllPropertiesMutation,
  useDeletePropertyMutation,
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useGetPropertiesQuery, // Keep this if used elsewhere
  useLazyGetPropertiesQuery,
  useLazyGetPropertyDetailQuery,
  useGetWatchingPropertyQuery, // Implicitly available, but can be explicit
  useLogPhoneRevealMutation,
  // useLazyGetWatchingPropertyQuery, // Implicitly available, but can be explicit
} = propertyApiService;

// For clarity, let's list the relevant generated hooks for watching properties
// They are generated automatically by RTK Query when type is 'query'
// export const { useGetWatchingPropertyQuery, useLazyGetWatchingPropertyQuery } = propertyApiService;
// However, the current export pattern is a single block.
// Adding them explicitly to the existing block if needed, or relying on them being available.
// Given the existing pattern, they are available without being re-exported here explicitly
// unless a specific one (e.g. non-lazy) is needed and not covered by a more general export.
// The existing code exports specific lazy queries and some non-lazy ones.
// We will rely on useLazyGetWatchingPropertyQuery being available as it was before,
// and useGetWatchingPropertyQuery also being available due to the type change.
// No change needed to the export list itself unless we want to be more explicit for the non-lazy version.
